import { useContext, useEffect, useState } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import GradientCircularProgressContainer from '../components/common/GradientCircularProgressContainer';
import { fetchUserAttributes, getCurrentUser } from 'aws-amplify/auth';
import { AUTHENTICATED } from '../constants/user';
import { ThemeContext } from './BaseThemeContextProvider';
import { handleUserSignOut, fetchOrgsAndAccounts } from '../utils/user';
import { useNavigate } from 'react-router-dom';
import { isDarkModePreferred } from '../utils/helpers';
import useBackendApi from '../hooks/useBackendApi';
import useOrgsAccts from '../hooks/useOrgsAccts';
import useSnackBarAlertMessages from '../hooks/useSnackBarAlertMessages';

export const useThemeContext = () => useContext(ThemeContext);

export const ThemeContextProvider = ({ children }) => {
  const useParentContext = useContext(ThemeContext);
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const [isAuthStatusReady, setIsAuthStatusReady] = useState(false);
  const [isUserDataReady, setIsUserDataReady] = useState(false);
  const [userType, setUserType] = useState('unauthenticated');
  const [cognitoUserInfo, setCognitoUserInfo] = useState({});
  const navigate = useNavigate();
  const { toggleTheme, isDarkMode } = useParentContext;
  const { apiFetchRequest } = useBackendApi();
  const { memberships, organizationMemberships, isMembershipDataReady } = useOrgsAccts(isUserDataReady, authStatus);

  const [errorText, setErrorText] = useState(null);
  const [loadingLocal, setLoadingLocal] = useState(false);
  const [orgs, setOrgs] = useState(undefined);
  const [accounts, setAccounts] = useState(undefined);
  const { addAlertMessages, renderSnackBarAlertComponent } = useSnackBarAlertMessages();

  const handleLogOut = async () => {
    await handleUserSignOut();
    setCognitoUserInfo({});
    navigate('/');
  };

  const updateCognitoUserInfo = (newCognitoUserData) => {
    setCognitoUserInfo(newCognitoUserData);
  };

  // This useEffect is created to handle the live update of the device's preferences
  // so we only need to define a single listener in the parent
  useEffect(() => {
    if (!cognitoUserInfo || !cognitoUserInfo?.attributes) return;

    const selectedTheme = cognitoUserInfo.attributes['custom:theme'];

    if ((selectedTheme === 'dark' && !isDarkMode) || (selectedTheme === 'light' && isDarkMode)) {
      toggleTheme();
    }

    if (selectedTheme === 'system') {
      const prefersDarkMode = isDarkModePreferred();
      if ((!prefersDarkMode && isDarkMode) || (prefersDarkMode && !isDarkMode)) {
        toggleTheme();
      }
    }
  }, [cognitoUserInfo, isDarkMode, toggleTheme]);

  useEffect(() => {
    if (authStatus !== 'configuring') {
      setIsAuthStatusReady(true);
    }

    if (!isAuthStatusReady) {
      return;
    }

    const getUserData = async () => {
      try {
        const currentUser = await getCurrentUser();
        const attributes = await fetchUserAttributes();
        if (attributes['custom:theme'] && attributes['custom:theme'] === 'dark' && !isDarkMode) {
          toggleTheme();
        }

        if (attributes['custom:theme'] && attributes['custom:theme'] === 'light' && isDarkMode) {
          toggleTheme();
        }

        setCognitoUserInfo({ userType: authStatus, currentUser, attributes });
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setIsUserDataReady(true);
      }
    };

    setUserType(authStatus);
    if (authStatus === AUTHENTICATED) {
      getUserData();
    } else {
      setIsUserDataReady(true);
    }
  }, [authStatus, isAuthStatusReady, isUserDataReady, isDarkMode]);

  useEffect(() => {
    const getOrgsAndAccounts = async () => {
      if (loadingLocal) {
        return;
      }
      await fetchOrgsAndAccounts(setErrorText, setLoadingLocal, apiFetchRequest, setOrgs, setAccounts, navigate);
    };
    if (!loadingLocal && isUserDataReady && authStatus === AUTHENTICATED) {
      getOrgsAndAccounts();
    }
  }, [isUserDataReady, authStatus]);

  useEffect(() => {
    addAlertMessages({
      message: errorText,
      severityType: 'error',
    });
  }, [errorText]);

  if (!isAuthStatusReady || authStatus !== userType || !isUserDataReady) {
    return <GradientCircularProgressContainer />;
  }

  return (
    <ThemeContext.Provider
      value={{
        ...useParentContext,
        userType,
        cognitoUserInfo,
        handleLogOut,
        updateCognitoUserInfo,
        memberships,
        organizationMemberships,
        isMembershipDataReady,
        errorText,
        orgs,
        accounts,
        loadingLocal,
        isUserDataReady,
      }}
    >
      {children}
      {renderSnackBarAlertComponent()}
    </ThemeContext.Provider>
  );
};
